import { type ReactNode, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useFocusTrap } from "./useFocusTrap";
import "./Modal.css";

interface ModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly titleId: string;
  readonly children: ReactNode;
}

/**
 * モーダル（O-2/O-3/O-5/O-6）。開いた時点でフォーカスを内部へ移し`Tab`を循環させ、
 * 閉じたら開く前の要素へ戻す（screen-flow.md §9.3）。`Esc`で閉じる。
 */
export function Modal({ open, onClose, titleId, children }: ModalProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  useFocusTrap(containerRef, open);

  useEffect(() => {
    if (!open) {
      return;
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        onClose();
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  return createPortal(
    <div className="modal-scrim">
      <div className="modal-scrim-backdrop" onClick={onClose} aria-hidden="true" />
      <div
        ref={containerRef}
        className="modal-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}
